export interface ProfileDateRange {
  start: string // ISO date string
  end: string // ISO date string
}

export interface User {
  userId: string
  accessLevel?: AccessLevel
  avatarUrl?: string
  color?: string
  firstName?: string
  status?:
    | 'ACTIVE'
    | 'DEACTIVATED'
    | 'SUSPENDED'
    | 'PENDING_COMPLETION'
    | 'NEEDS_APPROVAL'
  lastName?: string
  emailAddress?: string
  userRole?: string
  phone?: string
  schoolId?: string
  districtId?: string
  companyName?: string
  about?: string
  secondaryLanguages?: string[]
  gradeLevels?: string[]
  jobField?: string
  cities?: string[]
  connectionPreference?: string[]
  keywords?: string[]
  contentDescription?: string
  collaboratorDescription?: string
  publicBio?: string
  profileId?: string
  approvedAt?: string // Date when the profile was approved
  unavailableAt?: ProfileDateRange[] // Legacy field
  unavailabilityPeriods?: UnavailabilityPeriod[] // New field using the new API
  profileCompletionPercent?: number // Percentage of profile completion (0-100)
  profileComplete?: boolean // Whether the profile is 100% complete
}

export interface SignUpData {
  firstName?: string
  lastName?: string
  emailAddress?: string
  password?: string
  passwordHint?: string
  userRole: string
  companyName?: string
  schoolId?: string
  phone?: string
  communityCode?: string
}

export interface SignUpResponse {
  access_token: string
  refresh_token: string
}

export interface ProfileMatchInteraction {
  isFavorite: boolean
  isBlocked: boolean
  notes?: string
}

export interface ProfileMatchScore {
  relevance?: number
  distance?: number
}

export interface ProfileMatch {
  id: string
  color: string
  name: string
  title: string
  avatarUrl: string
  about?: string
  publicBio?: string
  jobField?: string
  companyName?: string
  keywords?: string[]
  createdAt?: string
  updatedAt?: string
  favorite?: boolean // For backward compatibility
  interaction?: ProfileMatchInteraction
  matchScore?: ProfileMatchScore
}

export interface EventLocation {
  id?: string
  name: string
  address?: string
  city?: string
  state?: string
  zip?: string
  lat?: number
  lng?: number
}

export interface EventAttachment {
  id: string
  name: string
  downloadUrl: string
  position?: number
}

export interface ProfileBrief {
  id: string
  firstName?: string
  lastName?: string
  avatarUrl?: string
  role?: string
  color?: string
}

export interface EventInvitationBrief {
  id: string
  status: string
  invited_at?: string
  responded_at?: string
  notes?: string
}

export interface EventDates {
  dates?: Array<{
    start: string
    end: string
  }>
  times?: Array<{
    start: string
    end: string
  }>
}

export interface Event {
  id: string
  name: string
  description?: string
  status: EventStatus | string
  created_at?: string
  updated_at?: string
  created_by?: string
  organized_by?: string
  is_organizer?: boolean

  // Detailed fields
  maximum_attendees?: number
  color?: string
  keywords?: string[]
  grade_levels?: string[]
  content_areas?: string[]
  engagement_opportunities?: string[]
  connection_preference?: string[]
  dates?: EventDates
  location?: EventLocation
  archived?: boolean
  open?: boolean
  flexible_dates?: boolean
  flexible_times?: boolean
  flexible_location?: boolean
  invite_all_educators?: boolean
  invite_all_professionals?: boolean
  total_ratings_count?: number
  total_ratings_score?: number
  expectation_statement?: string
  accomplish_statement?: string

  // Related data
  organizer?: ProfileBrief
  relatedInvitation?: EventInvitationBrief
  relatedRating?: EventRating // Will be populated when API is updated

  // Fields for UI functionality - not from API
  iconUrl?: string
  pendingInvitations?: EventInvitation[]
  acceptedInvitations?: EventInvitation[]
  cancelledInvitations?: EventInvitation[]
  reviewableInvitations?: EventInvitation[]
  finishedInvitations?: EventInvitation[]
  attachments?: EventAttachment[]
}

// Event status enum
export enum EventStatus {
  Draft = 'DRAFT',
  NeedsApproval = 'NEEDS_APPROVAL',
  Approved = 'APPROVED',
  Published = 'PUBLISHED',
  Cancelled = 'CANCELLED',
  Denied = 'DENIED',
  Finished = 'FINISHED',
  Completed = 'COMPLETED',
  Archived = 'ARCHIVED',
  Deleted = 'DELETED',
}

// Event invitation status enum
export enum EventInvitationStatus {
  Pending = 'PENDING',
  Accepted = 'ACCEPTED',
  Declined = 'DECLINED',
  Cancelled = 'CANCELLED',
  Finished = 'FINISHED',
  NeedsReview = 'NEEDS_REVIEW',
}

/**
 * EventRating interface for individual ratings
 */
export interface EventRating {
  id: string
  parent_id: string
  resource_type: string
  profile_id: string
  score: number | null
  comments?: string | null
  profile_name?: string | null
  profile_color: string
  profile_avatar_url: string
  created_at: string
  updated_at: string
}

/**
 * Paginated response for event ratings
 */
export interface EventRatingsResponse {
  ratings: EventRating[]
  total: number
  limit: number
  offset: number
  has_more: boolean
}

/**
 * Request body for submitting/updating a rating
 */
export interface SubmitRatingRequest {
  score: number
  comments?: string
}

/**
 * Admin statistics for event ratings
 */
export interface EventRatingStats {
  total_ratings: number
  average_score: number
  score_distribution: {
    [score: number]: number
  }
  recent_ratings_count: number
  last_updated: string
}

/**
 * Admin validation response for event ratings
 */
export interface EventRatingValidation {
  is_valid: boolean
  issues: string[]
  total_ratings: number
  aggregate_score: number
  last_checked: string
}

/**
 * Recalculation response
 */
export interface RatingRecalculationResponse {
  event_id: string
  total_ratings: number
  aggregate_score: number
  recalculated_at: string
}

/**
 * Bulk recalculation response
 */
export interface BulkRatingRecalculationResponse {
  total_events_processed: number
  successful_recalculations: number
  failed_recalculations: number
  errors: string[]
  started_at: string
  completed_at: string
}

/**
 * Report interfaces
 */
export enum ReportIndexType {
  Profiles = 'PROFILES',
  Events = 'EVENTS',
  Schools = 'SCHOOLS',
  Invitations = 'INVITATIONS',
}

export interface Report {
  id: string
  name: string
  description?: string
  queryString?: string
  query_string?: string
  facetFilters?: string[]
  facet_filters?: string[]
  indexType?: ReportIndexType
  index_type?: ReportIndexType | string
  createdAt?: string
  created_at?: string
  updatedAt?: string
  updated_at?: string
}

export interface ReportsResponse {
  reports: Report[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

export interface ReportsResponseOld {
  profiles: {
    reports: Report[]
  }
  events: {
    reports: Report[]
  }
  invitations: {
    reports: Report[]
  }
}

export interface CreateReportPayload {
  name: string
  description?: string
  queryString?: string
  facetFilters?: string[]
  indexType: ReportIndexType
}

export interface DownloadReportResponse {
  downloadUrl: string
}

/**
 * Admin User interfaces
 */
export enum AccessLevel {
  Admin = 'ADMIN',
  Moderator = 'MODERATOR',
  User = 'USER',
}

export interface PaginationEntry {
  page: number
  cursor: string
}

export interface AdminUser {
  id: string
  firstName: string | null // From User table, may be null
  lastName: string | null // From User table, may be null
  emailAddress: string
  accessLevel: AccessLevel
  profileId?: string
  profile?: AdminUserProfile // New: nested profile data
  createdAt: string
  updatedAt: string
}

export interface AdminUserProfile {
  id: string
  role: 'EDUCATOR' | 'PROFESSIONAL'
  firstName: string // From Profile table, always populated
  lastName: string // From Profile table, always populated
  emailAddress: string
  status: string
  createdAt?: string
}

export interface AdminUsersConnection {
  nodes: AdminUser[]
  pageInfo: {
    hasNextPages: PaginationEntry[]
    hasPreviousPages: PaginationEntry[]
    startCursor?: string
    endCursor?: string
  }
}

export interface AdminUsersResponse {
  admins: AdminUser[]
  pagination: {
    total: number
    limit: number
    offset: number
    hasMore: boolean
  }
}

export interface AdminUserResponse {
  response: {
    user: AdminUser
    errors?: string[]
  }
}

export interface CreateAdminUserPayload {
  firstName: string
  lastName: string
  emailAddress: string
  accessLevel: AccessLevel
}

export interface UpdateAdminUserPayload extends CreateAdminUserPayload {
  id: string
}

/**
 * Raw invitation object from the API
 */
export interface RawEventInvitation {
  id: string
  event_id: string
  profile_id: string
  status: EventInvitationStatus | string
  invited_at: string
  invited_by_profile_id?: string
  responded_at?: string | null
  notes?: string
}

/**
 * Raw API response for invitations
 */
export interface InvitationResponse {
  invitation: RawEventInvitation
  event: Event
}

/**
 * Processed EventInvitation object for use in the UI
 */
export interface EventInvitation {
  id: string
  eventId: string // Mapped from invitation.event_id
  profileId: string // Mapped from invitation.profile_id
  status: EventInvitationStatus | string
  invited_at?: string
  invited_by_profile_id?: string
  responded_at?: string | null
  notes?: string
  event?: Event // The full event object

  // Additional properties needed for drawer navigation
  eventName?: string
  eventColor?: string
  eventIconUrl?: string
  eventDates?: Array<{
    id: string
    startDate: string
    endDate: string
  }>

  // User profile properties (for attendee lists)
  profileName?: string
  profileColor?: string
  profileIconUrl?: string
}

// Thread Types
export enum ThreadType {
  USER = 'USER',
  EVENT = 'EVENT',
}

export enum ThreadFilterState {
  SENT = 'SENT',
  READ = 'READ',
  UNREAD = 'UNREAD',
  ALL = 'ALL',
}

export interface ThreadParticipant {
  profile_id: string
  first_name: string
  last_name: string
  avatar_url?: string
  color: string
}

export interface MessageAttachment {
  url: string
  type: string
  name: string
}

export interface ThreadMessage {
  id: string
  message_text: string
  sender_id: string
  sender_name?: string
  sender_avatar?: string
  attachments?: MessageAttachment[]
  read_by: string[]
  is_read?: boolean
  created_at: string
  updated_at?: string
}

export interface TargetParticipant {
  profile_id: string
  first_name: string
  last_name: string
  avatar_url?: string
  color: string
}

export interface TargetEvent {
  event_id: string
  name: string
  color: string
  image_url: string
}

export interface Thread {
  id: string
  thread_type: ThreadType
  entity_id: string
  associated_entity_id?: string
  title?: string
  last_message?: {
    id: string
    message_text: string
    sender_id: string
    sender_name: string
    read_by: string[]
    is_read?: boolean
    created_at: string
  }
  unread_count: number
  participants: ThreadParticipant[]
  participants_count: number
  target_participant?: TargetParticipant
  target_event?: TargetEvent
  messages?: ThreadMessage[]
  total_messages?: number
  created_at: string
  updated_at: string
}

export interface ThreadListResponse {
  threads: Thread[]
  total: number
  limit: number
  offset: number
}

export interface CreateThreadParams {
  thread_type: ThreadType
  entity_id: string
  associated_entity_id?: string
  title?: string
  participants: string[]
  initial_message?: string
}

export interface SendMessageParams {
  message_text: string
  attachments?: MessageAttachment[]
}

export interface ThreadListParams {
  filter?: ThreadFilterState
  limit?: number
  offset?: number
  sort?: 'newest' | 'oldest'
}

export interface ThreadDetailParams {
  thread_id: string
  limit?: number
  offset?: number
}

export interface SendMessageToThreadParams {
  thread_id: string
  message: SendMessageParams
}

export interface MarkMessageReadParams {
  thread_id: string
  message_id: string
}

export interface MarkThreadReadResponse {
  thread_id: string
  messages_updated: number
  updated_at: string
}

export interface ArchiveThreadResponse {
  thread_id: string
  messages_updated: number
  archived: boolean
  updated_at: string
}

/**
 * Unavailability period for a user profile
 */
export interface UnavailabilityPeriod {
  id: string
  profile_id: string
  start_date: string
  end_date: string
  description?: string
  created_at: string
  updated_at: string
}

/**
 * Request body for creating a new unavailability period
 */
export interface CreateUnavailabilityPeriodRequest {
  start_date: string
  end_date: string
  description?: string
}

/**
 * Request body for updating an unavailability period
 */
export interface UpdateUnavailabilityPeriodRequest {
  start_date?: string
  end_date?: string
  description?: string
}

/**
 * Resource Management Types
 */

export enum ResourceDocumentStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
}

export interface ResourceCategory {
  id: string
  name: string
  slug: string
  created_at: string
  updated_at: string
  documents?: ResourceDocument[]
}

export interface ResourceDocument {
  id: string
  title: string
  slug: string
  status: ResourceDocumentStatus
  color?: string
  content?: string
  content_html?: string
  content_preview?: string
  excerpt?: string
  featured: boolean
  icon_url?: string
  icon_key?: string
  category_id?: string
  category?: ResourceCategory
  category_name?: string
  category_slug?: string
  published_at?: string
  created_at: string
  updated_at: string
}

export interface ResourceDocumentConnection {
  nodes: ResourceDocument[]
  page_info: {
    has_next_page: boolean
    has_previous_page: boolean
    start_cursor?: string
    end_cursor?: string
  }
  total_count: number
}

export interface ResourceOperationResponse {
  success: boolean
  document?: ResourceDocument
  category?: ResourceCategory
  errors: Array<{
    field?: string
    message: string
    code?: string
  }>
}

export interface CreateResourceCategoryRequest {
  name: string
  slug?: string
}

export interface UpdateResourceCategoryRequest {
  name?: string
  slug?: string
}

export interface CreateResourceDocumentRequest {
  title: string
  slug?: string
  status: ResourceDocumentStatus
  color?: string
  content?: string
  content_html?: string
  content_preview?: string
  excerpt?: string
  featured?: boolean
  icon_key?: string
  category_id?: string
  published_at?: string
}

export interface UpdateResourceDocumentRequest {
  title?: string
  slug?: string
  status?: ResourceDocumentStatus
  color?: string
  content?: string
  content_html?: string
  content_preview?: string
  excerpt?: string
  featured?: boolean
  icon_key?: string
  category_id?: string
  published_at?: string
}

export interface ResourceDocumentFilters {
  limit?: number
  cursor?: string
  status?: string
  category_id?: string
  featured?: boolean
  search?: string
}

export interface IconUploadRequest {
  name: string
  contentType: string
}

export interface IconUploadResponse {
  success: boolean
  document?: {
    uploadUrl: string
    downloadUrl: string
    iconKey: string
  }
  category?: {
    uploadUrl: string
    downloadUrl: string
    iconKey: string
  }
  errors: Array<{
    field?: string
    message: string
    code?: string
  }>
}

export interface ResourceHealthResponse {
  status: 'healthy' | 'degraded' | 'unhealthy'
  overall_success_rate: number
  total_operations: number
  total_errors: number
  slow_operations: string[]
  operation_stats: Record<string, any>
  timestamp: string
}
